import React, { useState, useCallback, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { useWindowSize } from '../utils/useWindowSize';
import { getPackageSales } from '../utils/api';
import { FiPackage, FiTrendingUp, FiUsers, FiTag, FiRefreshCw } from 'react-icons/fi';

function fmtRupees(paise) {
  return '₹' + ((paise || 0) / 100).toLocaleString('en-IN');
}

function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
}

function initMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const PackageSales = () => {
  const { isMobile } = useWindowSize();
  const [month,   setMonth]   = useState(initMonth);
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getPackageSales(month);
      setData(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load package sales data.');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const contentStyle = {
    marginLeft: isMobile ? 0 : 260,
    padding: isMobile ? '16px' : '32px',
    minHeight: '100vh',
    background: '#f4f6f9',
    fontFamily: "'Open Sans', sans-serif"
  };

  const cardStyle = {
    background: '#fff',
    borderRadius: 10,
    padding: '20px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
    marginBottom: 24
  };

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <div style={contentStyle}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
          <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700, color: '#1a3d2b', fontFamily: "'Raleway', sans-serif" }}>
            Package Sales
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              type="month"
              value={month}
              onChange={function(e) { setMonth(e.target.value); }}
              style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, fontFamily: "'Open Sans', sans-serif", minHeight: 36 }}
            />
            <button
              onClick={loadData}
              disabled={loading}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#1a7a4a', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 14, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, minHeight: 36 }}
            >
              <FiRefreshCw style={{ fontSize: 14 }} />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div style={{ background: '#fee2e2', color: '#991b1b', padding: '12px 16px', borderRadius: 8, marginBottom: 20, fontSize: 14 }}>
            {error}
          </div>
        )}

        {loading && (
          <div style={{ textAlign: 'center', padding: 60, color: '#6b7280', fontSize: 15 }}>Loading...</div>
        )}

        {!loading && data && (
          <>
            {/* Stat cards */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
              {[
                { icon: <FiPackage />,    label: 'Packages Sold',   value: data.totalPackagesSold },
                { icon: <FiTrendingUp />, label: 'Total Revenue',   value: fmtRupees(data.totalRevenue) },
                { icon: <FiUsers />,      label: 'Unique Patients', value: data.uniquePatients },
                { icon: <FiTag />,        label: 'With Promo',      value: data.promoUsage.withPromo },
              ].map(function(card) {
                return (
                  <div key={card.label} style={{ background: '#fff', borderRadius: 10, padding: '20px 18px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                    <div style={{ color: '#1a7a4a', fontSize: 22, marginBottom: 8 }}>{card.icon}</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a3d2b', fontFamily: "'Raleway', sans-serif" }}>{card.value}</div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4, fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>{card.label}</div>
                  </div>
                );
              })}
            </div>

            {/* Package Breakdown */}
            <div style={cardStyle}>
              <h2 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 700, color: '#1a3d2b', fontFamily: "'Raleway', sans-serif" }}>Package Breakdown</h2>
              {data.packageBreakdown.length === 0 ? (
                <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>No packages sold this month.</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                        {['Package Name', 'Sold', 'Sessions', 'Revenue'].map(function(h) {
                          return (
                            <th key={h} style={{ textAlign: h === 'Package Name' ? 'left' : 'right', padding: '8px 12px', color: '#6b7280', fontWeight: 600, fontSize: 12, letterSpacing: '0.5px', textTransform: 'uppercase' }}>{h}</th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {data.packageBreakdown.map(function(b, i) {
                        return (
                          <tr key={String(b.packageId)} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                            <td style={{ padding: '10px 12px', color: '#1a3d2b', fontWeight: 600 }}>{b.packageName || '—'}</td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', color: '#374151' }}>{b.soldCount}</td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', color: '#374151' }}>{b.sessionsTotal}</td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', color: '#1a7a4a', fontWeight: 700 }}>{fmtRupees(b.revenue)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Promo Usage */}
            <div style={cardStyle}>
              <h2 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 700, color: '#1a3d2b', fontFamily: "'Raleway', sans-serif" }}>Promo Usage</h2>
              <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
                {[
                  { label: 'With Promo',          value: data.promoUsage.withPromo,    color: '#1a3d2b' },
                  { label: 'Without Promo',        value: data.promoUsage.withoutPromo, color: '#1a3d2b' },
                  { label: 'Total Discount Given', value: fmtRupees(data.promoUsage.totalDiscount), color: '#dc2626' },
                ].map(function(stat) {
                  return (
                    <div key={stat.label}>
                      <div style={{ fontSize: '1.4rem', fontWeight: 700, color: stat.color, fontFamily: "'Raleway', sans-serif" }}>{stat.value}</div>
                      <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, marginTop: 2 }}>{stat.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Recent Sales */}
            <div style={cardStyle}>
              <h2 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 700, color: '#1a3d2b', fontFamily: "'Raleway', sans-serif" }}>Recent Sales</h2>
              {data.recentSales.length === 0 ? (
                <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>No sales this month.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {data.recentSales.map(function(sale, i) {
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#f9fafb', borderRadius: 8, flexWrap: 'wrap', gap: 8 }}>
                        <div>
                          <div style={{ fontWeight: 600, color: '#1a3d2b', fontSize: 14 }}>{sale.patientFirstName || 'Patient'}</div>
                          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{sale.packageName}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 13, color: '#374151' }}>{fmtDate(sale.purchasedAt)}</div>
                          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{sale.sessionsTotal} sessions &middot; Expires {fmtDate(sale.expiresAt)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {!loading && !data && !error && (
          <div style={{ textAlign: 'center', padding: 60, color: '#6b7280', fontSize: 15 }}>No data available for this month.</div>
        )}

      </div>
    </div>
  );
};

export default PackageSales;
